import { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MonitoreoUso } from './components/avisos/MonitoreoUso';
import { AvisoVersionNueva } from '@renderer/components/layouts/AvisoVersionNueva';
import { sesionGuardada } from '@renderer/lib/sesionGuardada';
import { BandejaPedidosPage } from '@renderer/pages/BandejaPedidosPage';
import { VentasDelDiaPage } from '@renderer/pages/VentasDelDiaPage';
import { useNegocioStore } from './store/useNegocioStore';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { useSessionStore } from './store/useSessionStore';
import { RUTA_INICIAL, type Rol } from '@shared/permisos';
import { useUIPrefsStore } from './store/useUIPrefsStore';
import { MenuPublicoPage } from '@renderer/pages/MenuPublicoPage';
import { PedidoPublicoPage } from '@renderer/pages/PedidoPublicoPage';
import { AvisoPrivacidadPage } from '@renderer/pages/AvisoPrivacidadPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { PrimerosPasosPage } from '@renderer/pages/PrimerosPasosPage';
import { CategoriasPage } from './pages/CategoriasPage';
import { ProductosPage } from './pages/ProductosPage';
import { PaquetesPage } from './pages/PaquetesPage';
import { PromosPage } from './pages/PromosPage';
import { PosPage } from './pages/PosPage';
import { CocinaPage } from './pages/CocinaPage';
import { PedidosActivosPage } from './pages/PedidosActivosPage';
import { MostradorPage } from './pages/MostradorPage';
import { DespachoPage } from './pages/DespachoPage';
// Sprint 76 — El tablero de domicilios: la pantalla que se queda abierta junto a WhatsApp.
import { DomiciliosPage } from './pages/DomiciliosPage';
import { MesasPage } from './pages/MesasPage';
import { UsuariosPage } from './pages/UsuariosPage';
import { ReportesPage } from './pages/ReportesPage';
import { EstadisticasPage } from './pages/EstadisticasPage';
import { CambiosDeTurnoPage } from './pages/CambiosDeTurnoPage';
import { CorteCajaPage } from './pages/CorteCajaPage';
import { InventarioDelDiaPage } from './pages/InventarioDelDiaPage';
import { CierreDelDiaPage } from './pages/CierreDelDiaPage';
import { CerrarDiaWizardPage } from './pages/CerrarDiaWizardPage';
import { PrecargarInventarioPage } from './pages/PrecargarInventarioPage';
import { SettingsPage } from './pages/SettingsPage';
import { PropinasPage } from './pages/PropinasPage';
import { AppLayout } from './components/layouts/AppLayout';
import { DialogsProvider } from './components/ui/DialogsProvider';
import { ErrorBoundary } from './components/ui/ErrorBoundary';

export function App() {
  const cargarNegocio = useNegocioStore((s) => s.cargar);
  const negocio = useNegocioStore((s) => s.negocio);
  const cargandoNegocio = useNegocioStore((s) => s.cargando);
  const sesion = useSessionStore((s) => s.sesion);
  // Sprint 40 — Accesibilidad: cargar prefs UI antes de pintar para evitar
  // FOUC (parpadeo con la escala default si la guardada era 'grande').
  const cargarUIPrefs = useUIPrefsStore((s) => s.cargar);
  // Sprint 64 — Los tres ajustes de permisos del negocio, para que la pantalla no
  // ofrezca botones que el servidor va a rechazar. Se cargan al haber sesión, no en el
  // arranque: `config:obtener` exige sesión y antes del login no hay ninguna.
  const cargarAjustesPermisos = useAjustesPermisos((s) => s.cargar);
  // Sprint 63 — El oficio de quien está usando el sistema, para el ruteo por default.
  const rolActual = (sesion?.rol as Rol) ?? 'mesero';
  const [estadoOnboarding, setEstadoOnboarding] = useState<
    'cargando' | 'pendiente' | 'completo' | 'sin_conexion'
  >('cargando');
  const sinConexion = useNegocioStore((s) => s.sinConexion);

  const setSesion = useSessionStore((s) => s.setSesion);

  useEffect(() => {
    (async () => {
      // Sprint 166 — Antes de decidir si se pide el PIN: ¿ya había alguien dentro? (recarga)
      const yaDentro = await sesionGuardada();
      if (yaDentro) setSesion(yaDentro);
      const res = await window.api.onboarding.estado();
      if (res.ok) {
        setEstadoOnboarding(res.data.completado ? 'completo' : 'pendiente');
      } else {
        /*
          Sprint 158 (DT-132) — Antes: 'pendiente'. Una base que no responde abría "Configuración
          inicial 1/5": a la dueña se le decía que su negocio no existía, con un formulario para
          fundarlo otra vez (el paso 4 crea un dueño). No saber no es lo mismo que "no hay".
        */
        setEstadoOnboarding('sin_conexion');
      }
      await Promise.all([cargarNegocio(), cargarUIPrefs()]);
    })();
  }, [cargarNegocio, cargarUIPrefs, setSesion]);

  useEffect(() => {
    if (!sesion) return;
    void cargarAjustesPermisos();
  }, [sesion, cargarAjustesPermisos]);

  if (estadoOnboarding === 'cargando' || cargandoNegocio) {
    return <PantallaCarga />;
  }

  if (estadoOnboarding === 'sin_conexion' || sinConexion) {
    return <PantallaSinConexion />;
  }

  return (
    <ErrorBoundary>
    <DialogsProvider>
    {/*
      Sprint 116 — Vive FUERA del router y de los guardias de sesión, a propósito.

      El POS se abre en la mañana y se cierra a medianoche: sin esto, un despliegue correcto puede
      tardar un día entero en llegarle a quien ya tenía la pestaña abierta. Al estar aquí avisa en
      cualquier pantalla —incluido el enlace público del cliente— y sobrevive a los cambios de ruta.
    */}
    <AvisoVersionNueva />
    <HashRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      {/* Sprint 161 — Monitoreo de uso: necesita el router para saber en qué pantalla se está. */}
      <MonitoreoUso />
      <Routes>
        {/*
          Sprint 92 — El menú público va **antes de los dos candados**, a propósito.

          Es la única ruta de la app que no exige sesión ni negocio configurado: el cliente que abre
          el enlace desde WhatsApp no tiene cuenta ni la va a tener. Quien decide si existe o no es
          el propio endpoint (`menuPublicoActivo`), del lado del servidor; esta ruta sólo la dibuja.
        */}
        <Route path="/menu" element={<MenuPublicoPage />} />
        {/* Sprint 96 — Su pedido, con un token que no se puede adivinar. Tampoco pide sesión. */}
        <Route path="/pedido/:token" element={<PedidoPublicoPage />} />
        {/* Sprint 167 — El aviso de privacidad del enlace. Público, como el menú. */}
        <Route path="/privacidad" element={<AvisoPrivacidadPage />} />
        {estadoOnboarding === 'pendiente' || !negocio ? (
          <>
            <Route path="/onboarding" element={<OnboardingPage onComplete={() => {
              setEstadoOnboarding('completo');
              cargarNegocio();
            }} />} />
            <Route path="*" element={<Navigate to="/onboarding" replace />} />
          </>
        ) : !sesion ? (
          <>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <>
            <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
            <Route path="/pos" element={<AppLayout><PosPage /></AppLayout>} />
            <Route path="/cocina" element={<AppLayout><CocinaPage /></AppLayout>} />
            {/*
              Sprint 123 — La bandeja unica. `/pedidos` sigue existiendo (es la lista con
              filtros, buscador y el detalle de cada pedido); esto es el tablero de trabajo.
            */}
            <Route path="/bandeja" element={<AppLayout><BandejaPedidosPage /></AppLayout>} />
            {/* Sprint 145 — "¿Dónde veo lo que ya vendí?", preguntado por la dueña vendiendo. */}
            <Route path="/ventas" element={<AppLayout><VentasDelDiaPage /></AppLayout>} />
            <Route path="/pedidos" element={<AppLayout><PedidosActivosPage /></AppLayout>} />
            <Route path="/domicilios" element={<AppLayout><DomiciliosPage /></AppLayout>} />
            {/* Sprint 108 — Venta de mostrador: el cliente pasa a recogerlo. */}
            <Route path="/mostrador" element={<AppLayout><MostradorPage /></AppLayout>} />
            <Route path="/despacho" element={<AppLayout><DespachoPage /></AppLayout>} />
            <Route path="/mesas" element={<AppLayout><MesasPage /></AppLayout>} />
            {/* Sprint 172 — La bitácora pide el PIN adentro; el servidor decide quién la ve (caja y dueña). */}
            <Route path="/cambios-de-turno" element={<AppLayout><CambiosDeTurnoPage /></AppLayout>} />
            <Route path="/primeros-pasos" element={<AppLayout><RequireDueno><PrimerosPasosPage /></RequireDueno></AppLayout>} />
            <Route path="/categorias" element={<AppLayout><RequireDueno><CategoriasPage /></RequireDueno></AppLayout>} />
            <Route path="/productos" element={<AppLayout><RequireDueno><ProductosPage /></RequireDueno></AppLayout>} />
            <Route path="/paquetes" element={<AppLayout><RequireDueno><PaquetesPage /></RequireDueno></AppLayout>} />
            <Route path="/promos" element={<AppLayout><RequireDueno><PromosPage /></RequireDueno></AppLayout>} />
            <Route path="/usuarios" element={<AppLayout><RequireDueno><UsuariosPage /></RequireDueno></AppLayout>} />
            <Route path="/reportes" element={<AppLayout><RequireDueno><ReportesPage /></RequireDueno></AppLayout>} />
            <Route path="/estadisticas" element={<AppLayout><RequireDueno><EstadisticasPage /></RequireDueno></AppLayout>} />
            <Route path="/corte-caja" element={<AppLayout><RequireDueno><CorteCajaPage /></RequireDueno></AppLayout>} />
            <Route path="/inventario" element={<AppLayout><RequireDueno><InventarioDelDiaPage /></RequireDueno></AppLayout>} />
            <Route path="/precargar-inventario" element={<AppLayout><RequireDueno><PrecargarInventarioPage /></RequireDueno></AppLayout>} />
            <Route path="/cierre-dia" element={<AppLayout><RequireDueno><CierreDelDiaPage /></RequireDueno></AppLayout>} />
            <Route path="/cerrar-dia" element={<AppLayout><RequireDueno><CerrarDiaWizardPage /></RequireDueno></AppLayout>} />
            <Route path="/settings" element={<AppLayout><RequireDueno><SettingsPage /></RequireDueno></AppLayout>} />
            <Route path="/propinas" element={<AppLayout><RequireDueno><PropinasPage /></RequireDueno></AppLayout>} />
            {/*
              Sprint 63 (Fase 5) — Cada oficio cae en SU trabajo: cocina en la cola de
              preparación, mesero en el salón. Mandar a todos al tablero significaba que
              quien sólo prepara comida abría una pantalla de ventas que no puede leer —y
              cuyas consultas su rol ya no autoriza.
            */}
            <Route path="*" element={<Navigate to={RUTA_INICIAL[rolActual] ?? '/dashboard'} replace />} />
          </>
        )}
      </Routes>
    </HashRouter>
    </DialogsProvider>
    </ErrorBoundary>
  );
}

function PantallaCarga() {
  return (
    <div className="flex h-full items-center justify-center text-slate-500">
      <div className="text-center">
        <div className="mb-3 text-2xl font-bold text-primary">RestaurantPOS</div>
        <div className="text-sm">Cargando…</div>
      </div>
    </div>
  );
}

/** Sprint 158 (DT-132) — Lo que se ve si el sistema no pudo preguntar por el negocio. */
function PantallaSinConexion() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div role="alert" className="max-w-md rounded-xl border-2 border-amber-400 bg-amber-50 p-6 text-center">
        <p className="text-xl font-bold text-amber-950">No se pudo conectar con el sistema</p>
        <p className="mt-2 text-base text-amber-950">
          Tus datos están a salvo: sólo que ahorita no responde. Revisa que haya internet y vuelve a
          intentar en un momento.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 min-h-[48px] rounded-lg bg-amber-700 px-6 text-base font-bold text-white hover:bg-amber-800"
        >
          Volver a intentar
        </button>
      </div>
    </div>
  );
}

function RequireDueno({ children }: { children: React.ReactNode }) {
  const sesion = useSessionStore((s) => s.sesion);
  if (sesion?.rol !== 'dueño') {
    // Sprint 63 — De vuelta a su trabajo. Antes mandaba al tablero, que un cocinero
    // tampoco puede ver: un rebote entre dos pantallas prohibidas.
    return <Navigate to={RUTA_INICIAL[(sesion?.rol as Rol) ?? 'mesero'] ?? '/mesas'} replace />;
  }
  return <>{children}</>;
}

